<template>
  <div class="cs-card">
    <div class="cs-card-head">
      <h3>自动择日提交信息</h3>
      <span class="cs-total">共 {{ list.length }} 条</span>

      <div class="cs-head-right">
        <el-select v-model="query.status" placeholder="状态" clearable size="small" style="width:130px" @change="fetch">
          <el-option label="全部状态" value="" />
          <el-option label="待审核" value="0" />
          <el-option label="已通过" value="1" />
          <el-option label="无日课" value="2" />
        </el-select>
        <el-input
          v-model="query.keyword"
          size="small"
          placeholder="用户ID / 用户名 / 姓名"
          clearable
          style="width:210px"
          @keyup.enter.native="fetch"
          @clear="fetch"
        />
        <el-button size="small" type="primary" icon="el-icon-search" @click="fetch">查询</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>
    </div>

    <div class="cs-card-body">
      <el-table :data="list" v-loading="loading" border stripe size="small" style="width:100%">
        <el-table-column prop="userId" label="用户ID" width="100" align="center" />
        <el-table-column prop="userName" label="用户名" min-width="140" show-overflow-tooltip>
          <template slot-scope="s">
            <span>{{ s.row.userName || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" width="180">
          <template slot-scope="s">{{ formatTime(s.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="s">
            <span class="cs-tag" :class="'cs-tag-' + statusKey(s.row.type)">{{ statusText(s.row.type) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center">
          <template slot-scope="s">
            <el-button size="mini" @click="openDetail(s.row)">查看</el-button>
            <!-- 仅「待审核」可操作：已通过 / 无日课 均禁用 -->
            <el-button
              size="mini"
              type="success"
              :disabled="statusKey(s.row.type) !== '0'"
              @click="changeType(s.row, '1')"
            >通过</el-button>
            <el-button
              size="mini"
              type="danger"
              :disabled="statusKey(s.row.type) !== '0'"
              @click="changeType(s.row, '2')"
            >拒绝</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && !list.length" class="cs-empty">暂无提交信息</div>
    </div>

    <!-- 详情：排布对齐客户提交页左侧表单；除「用事时间段」「允许用事日期期限范围」外均只读 -->
    <el-dialog title="提交信息详情" :visible.sync="detailVisible" width="720px">
      <div v-if="detail" class="cs-detail">
        <!-- 只读区 -->
        <el-form size="small" label-width="170px" class="cs-detail-form" disabled>
          <el-form-item label="吉时分类:">
            <span class="cs-readonly">{{ displayClassify(detail.classifyVal) }}</span>
          </el-form-item>
          <el-form-item v-if="detail.zuoshan" label="选择坐山(向或方):">
            <span class="cs-readonly">{{ detail.zuoshan }}</span>
          </el-form-item>
          <el-form-item label="姓名:">
            <el-input :value="detail.name" style="width:220px" />
          </el-form-item>
          <el-form-item label="性别:">
            <el-radio :value="detail.sex" label="男">男</el-radio>
            <el-radio :value="detail.sex" label="女">女</el-radio>
          </el-form-item>
          <el-form-item label="福主本命(属相):">
            <el-checkbox-group :value="fuzhuValues(detail.fuzhu)">
              <el-checkbox v-for="(item, key) in gongList" :key="key" :label="item.value">{{ item.key }}</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="用事地点:">
            <span class="cs-readonly">{{ displayPlace(detail.place) }}</span>
          </el-form-item>
          <el-form-item label="经度:">
            <span class="cs-readonly">{{ displayDms(detail.longitude, ['度', '分', '秒']) }}</span>
          </el-form-item>
          <el-form-item label="纬度:">
            <span class="cs-readonly">{{ displayDms(detail.latitude, ['度', '分', '秒']) }}</span>
          </el-form-item>
        </el-form>

        <!-- 可编辑区：仅「待审核」可调整「用事时间段」与「允许用事日期期限范围」，
             二者修改方式与生效条件（审核前、随通过/拒绝一并保存）保持一致 -->
        <el-form size="small" label-width="170px" class="cs-detail-form">
          <el-form-item label="用事时间段:">
            <el-radio-group v-model="editTimerange" :disabled="!canReview">
              <el-radio v-for="opt in timerangeOptions" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio>
            </el-radio-group>
            <span v-if="timerangeDirty" class="cs-dirty">已修改</span>
          </el-form-item>
          <el-form-item label="允许用事日期期限范围:">
            <el-date-picker
              v-model="editActRange[0]"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              type="date"
              placeholder="开始日期"
              :disabled="!canReview"
              style="width:170px"
            />
            <span class="cs-gap">至</span>
            <el-date-picker
              v-model="editActRange[1]"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              type="date"
              placeholder="结束日期"
              :disabled="!canReview"
              style="width:170px"
            />
            <span v-if="rangeDirty" class="cs-dirty">已修改</span>
          </el-form-item>
        </el-form>

        <!-- 状态信息 -->
        <div class="cs-detail-status">
          <div class="cs-detail-status-item">
            <span class="cs-detail-label">状态</span>
            <span class="cs-tag" :class="'cs-tag-' + statusKey(detail.type)">{{ statusText(detail.type) }}</span>
          </div>
          <div class="cs-detail-status-item">
            <span class="cs-detail-label">提交时间</span>
            <span>{{ formatTime(detail.createdAt) }}</span>
          </div>
          <div class="cs-detail-status-item">
            <span class="cs-detail-label">审核时间</span>
            <span>{{ formatTime(detail.updatedAt) }}</span>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
        <!-- 仅「待审核」可操作：已通过 / 无日课 均禁用 -->
        <el-button size="small" type="success" :loading="saving" :disabled="!canReview" @click="review('1')">通过</el-button>
        <el-button size="small" type="danger" :loading="saving" :disabled="!canReview" @click="review('2')">拒绝</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 注意：element-china-area-data v5 只导出 CodeToText（大写），没有小写 codeToText；
// 用小写会在渲染期抛 TypeError，导致详情弹窗渲染失败（点了没反应）。
import { CodeToText } from 'element-china-area-data'
import { selectionApi } from '../api/cs'

const AREA_TEXT = CodeToText || {}

// 与客户提交页左侧表单一致的本命选项（value 为存库值，key 为显示文字）
const GONG_LIST = [
  { key: '鼠', value: '子' }, { key: '牛', value: '丑' }, { key: '虎', value: '寅' },
  { key: '兔', value: '卯' }, { key: '龙', value: '辰' }, { key: '蛇', value: '巳' },
  { key: '马', value: '午' }, { key: '羊', value: '未' }, { key: '猴', value: '申' },
  { key: '鸡', value: '酉' }, { key: '狗', value: '戌' }, { key: '猪', value: '亥' }
]

// 「用事时间段」可选项，文案与客户提交页 用事时间段 单选项保持一致
const TIMERANGE_OPTIONS = [
  { value: '上午', label: '上午（6:00:00-11:59:59）' },
  { value: '下午', label: '下午（12:00:17:59:59）' },
  { value: '晚上', label: '晚上（18:00:00-23:59:59）' },
  { value: '凌晨', label: '凌晨（00:00:00-05:59:59）' }
]

export default {
  name: 'CsSelectionOrders',
  data () {
    return {
      loading: false,
      saving: false,
      query: { status: '', keyword: '' },
      list: [],
      detailVisible: false,
      detail: null,
      gongList: GONG_LIST,
      timerangeOptions: TIMERANGE_OPTIONS,
      // 详情内可编辑的用事时间段
      editTimerange: '',
      originTimerange: '',
      // 详情内可编辑的用事日期期限范围
      editActRange: ['', ''],
      originActRange: ''
    }
  },
  computed: {
    // 仅「待审核」允许审核：已通过 / 无日课 时禁用通过、拒绝以及用事时间段、日期修改
    canReview () {
      return !!this.detail && this.statusKey(this.detail.type) === '0'
    },
    timerangeDirty () {
      return this.editTimerange !== this.originTimerange
    },
    rangeDirty () {
      return this.joinRange(this.editActRange) !== this.originActRange
    }
  },
  created () {
    this.fetch()
  },
  methods: {
    async fetch () {
      this.loading = true
      try {
        const res = await selectionApi.list({ status: this.query.status || '', keyword: this.query.keyword || '' })
        this.list = Array.isArray(res) ? res : ((res && res.list) || [])
      } catch (e) {
        this.list = []
        this.$message.error(e.uiMessage || e.message)
      } finally {
        this.loading = false
      }
    },
    reset () {
      this.query.status = ''
      this.query.keyword = ''
      this.fetch()
    },
    async openDetail (row) {
      let d = row
      try {
        d = await selectionApi.detail(row.id)
      } catch (e) {
        d = row // 详情接口异常时退回列表行数据
      }
      this.detail = d
      this.editTimerange = d.timerange == null ? '' : String(d.timerange)
      this.originTimerange = this.editTimerange
      const range = this.parseRange(d.actRange)
      this.editActRange = [range[0], range[1]]
      this.originActRange = this.joinRange(this.editActRange)
      this.detailVisible = true
    },
    // 审核：用事时间段 / 日期期限范围有改动则连同 actRange、timerange 一起提交，否则只改状态
    async review (type) {
      if (!this.detail) return
      if (!this.canReview) {
        this.$message.warning('该记录已审核，不能再操作')
        return
      }
      const label = type === '1' ? '通过' : '拒绝'
      const timerangeChanged = this.timerangeDirty
      const rangeChanged = this.rangeDirty
      const changed = timerangeChanged || rangeChanged
      if (timerangeChanged && !this.editTimerange) {
        this.$message.warning('请选择用事时间段')
        return
      }
      if (rangeChanged) {
        if (!this.editActRange[0] || !this.editActRange[1]) {
          this.$message.warning('请完整选择用事日期期限范围的开始与结束日期')
          return
        }
        if (this.editActRange[1] < this.editActRange[0]) {
          this.$message.warning('结束日期不能早于开始日期')
          return
        }
      }
      const changedTexts = []
      if (timerangeChanged) changedTexts.push(`用事时间段改为「${this.editTimerange}」`)
      if (rangeChanged) changedTexts.push(`用事日期期限范围改为 ${this.editActRange[0]} 至 ${this.editActRange[1]}`)
      const tip = changed
        ? `确认将该提交信息标记为「${label}」，并把${changedTexts.join('，同时把')}？`
        : `确认将该提交信息标记为「${label}」？`
      try {
        await this.$confirm(tip, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return // 用户取消
      }
      this.saving = true
      try {
        const payload = { type }
        if (timerangeChanged) payload.timerange = this.editTimerange
        if (rangeChanged) payload.actRange = this.joinRange(this.editActRange)
        await selectionApi.updateType(this.detail.id, payload)
        this.$message.success(changed ? `已${label}，提交信息已更新` : `已${label}`)
        this.detailVisible = false
        this.fetch()
      } catch (e) {
        this.$message.error(e.uiMessage || e.message)
      } finally {
        this.saving = false
      }
    },
    // 列表行内的快捷通过/拒绝：仅改状态
    async changeType (row, type) {
      if (!row || row.id == null) return
      if (this.statusKey(row.type) !== '0') {
        this.$message.warning('该记录已审核，不能再操作')
        return
      }
      const label = type === '1' ? '通过' : '拒绝'
      try {
        await this.$confirm(`确认将该提交信息标记为「${label}」？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        await selectionApi.updateType(row.id, { type })
        this.$message.success(`已${label}`)
        this.fetch()
      } catch (e) {
        this.$message.error(e.uiMessage || e.message)
      }
    },
    // ---------------- 展示格式化 ----------------
    parseRange (v) {
      const parts = String(v == null ? '' : v).split(',').map(s => s.trim())
      return [parts[0] || '', parts[1] || '']
    },
    joinRange (arr) {
      const a = (arr && arr[0]) || ''
      const b = (arr && arr[1]) || ''
      return a && b ? `${a},${b}` : ''
    },
    displayClassify (v) {
      if (!v) return '—'
      return String(v).split(',').filter(Boolean).join(' / ') || '—'
    },
    displayPlace (v) {
      if (!v) return '—'
      const names = String(v).split(',').map(code => AREA_TEXT[code.trim()] || code.trim())
      return names.filter(Boolean).join(' ') || '—'
    },
    displayDms (v, units) {
      const parts = String(v == null ? '' : v).split(',').map(s => s.trim()).filter(s => s !== '')
      if (!parts.length) return '—'
      return parts.map((n, i) => n + ' ' + (units[i] || '')).join(' ')
    },
    fuzhuValues (v) {
      return String(v == null ? '' : v).split(',').map(s => s.trim()).filter(Boolean)
    },
    statusKey (type) {
      const t = String(type == null ? '' : type)
      if (t === '1') return '1'
      if (t === '2') return '2'
      return '0'
    },
    // 状态文案：'2' 按业务语义显示为「无日课」（后端字段值仍为 '2'，与客户页口径一致）
    statusText (type) {
      const k = this.statusKey(type)
      if (k === '1') return '已通过'
      if (k === '2') return '无日课'
      return '待审核'
    },
    formatTime (v) {
      if (!v) return '—'
      return String(v).replace('T', ' ').split('.')[0]
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-detail {
  .cs-detail-form {
    padding: 4px 0;

    .cs-readonly {
      color: #1f2937;
      line-height: 32px;
    }

    .cs-gap {
      display: inline-block;
      padding: 0 8px;
      color: #8c9bb0;
    }

    .cs-dirty {
      margin-left: 10px;
      font-size: 12px;
      color: #f59e0b;
    }
  }

  .cs-detail-status {
    display: flex;
    flex-wrap: wrap;
    gap: 26px;
    margin-top: 4px;
    padding: 12px 16px;
    background: #f8fafc;
    border: 1px solid #e9edf4;
    border-radius: 10px;
    font-size: 13px;

    .cs-detail-status-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .cs-detail-label { color: #8c9bb0; }
  }
}
</style>
